import React, { useState } from 'react';
import { useTranslation } from '../../i18n';

interface QuestionFeedbackSectionProps {
  questionId: string;
  roomName: string;
  gameType: string;
  answerSubmitted: boolean;
  showFeedback: boolean;
  onSubmitFeedback: (rating?: number, correct?: boolean, questionText?: string, correctAnswer?: string) => void;
  questionText?: string;
  correctAnswer?: string;
}

const STAR_FILLED = '⭐';
const STAR_EMPTY = '☆';

const buttonClass = (selected: boolean | undefined, isYes: boolean): string => {
  const base = 'question-feedback-section__btn';
  if (selected === isYes) {
    return `${base} question-feedback-section__btn--selected`;
  }
  return base;
};

const QuestionFeedbackSection: React.FC<QuestionFeedbackSectionProps> = ({
  questionId,
  roomName,
  gameType,
  answerSubmitted,
  showFeedback,
  onSubmitFeedback,
  questionText,
  correctAnswer,
}) => {
  const { t } = useTranslation();
  const [rating, setRating] = useState<number | undefined>(undefined);
  const [correct, setCorrect] = useState<boolean | undefined>(undefined);
  const [showThanks, setShowThanks] = useState(false);
  const [hoverStar, setHoverStar] = useState<number | null>(null);

  const isLowRating = rating === 1 || rating === 2;
  const showIncorrectQuestion = isLowRating && rating !== undefined && correct === undefined;
  const effectiveStars = rating ?? hoverStar ?? 0;

  const handleStarClick = (value: number) => {
    if (rating !== undefined) return;
    setRating(value);
    if (value >= 3) {
      onSubmitFeedback(value, undefined, questionText, correctAnswer);
      setShowThanks(true);
    }
  };

  const handleCorrect = (value: boolean) => {
    if (correct !== undefined) return;
    setCorrect(value);
    onSubmitFeedback(rating!, value, questionText, correctAnswer);
    setShowThanks(true);
  };

  if (!answerSubmitted || !showFeedback) {
    return null;
  }

  return (
    <div
      className="question-feedback-section"
      data-testid="question-feedback-section"
      style={{
        width: '100%',
        marginTop: '1.5rem',
        padding: '1.25rem',
        backgroundColor: 'rgba(255, 255, 255, 0.88)',
        borderRadius: '12px',
        border: '1.5px solid var(--border-accent)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <style>{`
        @media (min-width: 769px) and (max-width: 1024px) {
          .question-feedback-section {
            margin-bottom: 2.5rem;
          }
        }
      `}</style>
      <div style={{ marginBottom: rating !== undefined && showIncorrectQuestion ? '1.25rem' : 0 }}>
        {rating === undefined ? (
          <>
            <p className="question-feedback-section__label" style={{ fontSize: '0.95rem', marginBottom: '0.6rem', textAlign: 'center' }}>
              {t('gameplay.feedback.didYouLikeQuestion')}
            </p>
            <div
              style={{
                display: 'flex',
                gap: '0.25rem',
                flexWrap: 'wrap',
                justifyContent: 'center',
                alignItems: 'center',
              }}
              role="group"
              aria-label={t('gameplay.feedback.rateStarsAria')}
            >
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  data-rating={value}
                  onClick={() => handleStarClick(value)}
                  onMouseEnter={(e) => {
                    setHoverStar(value);
                    e.currentTarget.style.transform = 'scale(1.2)';
                  }}
                  onMouseLeave={(e) => {
                    setHoverStar(null);
                    e.currentTarget.style.transform = 'scale(1)';
                  }}
                  style={{
                    padding: '0.4rem',
                    fontSize: '1.75rem',
                    lineHeight: 1,
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    opacity: 1,
                    transition: 'transform 0.15s ease',
                  }}
                  aria-label={t(value === 1 ? 'gameplay.feedback.starAria_one' : 'gameplay.feedback.starAria_other', { count: value })}
                  title={t(value === 1 ? 'gameplay.feedback.starAria_one' : 'gameplay.feedback.starAria_other', { count: value })}
                >
                  {value <= effectiveStars ? STAR_FILLED : STAR_EMPTY}
                </button>
              ))}
            </div>
          </>
        ) : showIncorrectQuestion ? null : showThanks && rating !== undefined && rating >= 3 ? (
          <p className="question-feedback-section__label" style={{ fontSize: '0.95rem', margin: 0, textAlign: 'center' }}>
            {t('gameplay.feedback.thankYou')}
          </p>
        ) : null}
      </div>

      {showIncorrectQuestion && (
        <div style={{ marginBottom: '0.5rem' }}>
          <p className="question-feedback-section__label" style={{ fontSize: '0.95rem', marginBottom: '0.6rem', textAlign: 'center' }}>
            {t('gameplay.feedback.lowRatingFollowUp')}
          </p>
          <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={() => handleCorrect(true)}
              className={buttonClass(correct, true)}
              style={{
                padding: '0.6rem 1.2rem',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '10px',
                cursor: correct !== undefined ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {t('common.yes')}
            </button>
            <button
              type="button"
              onClick={() => handleCorrect(false)}
              className={buttonClass(correct, false)}
              style={{
                padding: '0.6rem 1.2rem',
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: '10px',
                cursor: correct !== undefined ? 'default' : 'pointer',
                transition: 'all 0.2s ease',
              }}
            >
              {t('common.no')}
            </button>
          </div>
        </div>
      )}

      {showThanks && isLowRating && correct !== undefined && (
        <p className="question-feedback-section__label" style={{ fontSize: '0.95rem', margin: 0, marginTop: '1rem', textAlign: 'center' }}>
          {t('gameplay.feedback.thankYou')}
        </p>
      )}
    </div>
  );
};

export default QuestionFeedbackSection;
